import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
  Box,
  Typography,
  Divider
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DroppableProvided, DraggableProvided } from 'react-beautiful-dnd';
import { DragOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

export interface ColumnSetting {
  field: string;
  headerName: string;
  visible: boolean;
  order: number;
}

interface OrderGridColumnSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (columns: ColumnSetting[]) => void;
  initialColumns: ColumnSetting[];
}

export default function OrderGridColumnSettingsDialog({
  open,
  onClose,
  onSave,
  initialColumns
}: OrderGridColumnSettingsDialogProps) {
  const [columns, setColumns] = useState<ColumnSetting[]>(initialColumns);

  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  const handleToggleColumn = (field: string) => {
    setColumns(prev => 
      prev.map(col => 
        col.field === field 
          ? { ...col, visible: !col.visible }
          : col
      )
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(columns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order numbers for visible columns only
    const visibleColumns = items.filter(col => col.visible);
    const hiddenColumns = items.filter(col => !col.visible);
    
    const updatedVisibleColumns = visibleColumns.map((item, index) => ({
      ...item,
      order: index
    }));

    // Keep hidden columns at the end with their original order
    const updatedHiddenColumns = hiddenColumns.map((item, index) => ({
      ...item,
      order: visibleColumns.length + index
    }));

    const updatedItems = [...updatedVisibleColumns, ...updatedHiddenColumns];
    setColumns(updatedItems);
  };

  const handleSave = () => {
    onSave(columns);
    onClose();
  };

  const handleCancel = () => {
    setColumns(initialColumns);
    onClose();
  };

  const handleShowAll = () => {
    setColumns(prev => 
      prev.map(col => ({ ...col, visible: true }))
    );
  };

  const handleHideAll = () => {
    setColumns(prev => 
      prev.map(col => ({ ...col, visible: false }))
    );
  };

  const visibleColumns = columns.filter(col => col.visible);
  const hiddenColumns = columns.filter(col => !col.visible);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Order Grid Column Settings</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag to reorder visible columns. Use checkboxes to show/hide columns.
        </Typography>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleShowAll}
            sx={{ flex: 1 }}
          >
            Show All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleHideAll}
            sx={{ flex: 1 }}
          >
            Hide All
          </Button>
        </Box>

        {/* Visible Columns */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Visible Columns ({visibleColumns.length})
        </Typography>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="visible-columns">
            {(provided: DroppableProvided) => (
              <List
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ 
                  minHeight: '100px',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 2
                }}
              >
                {visibleColumns.map((column, index) => (
                  <Draggable key={column.field} draggableId={column.field} index={index}>
                    {(provided: DraggableProvided) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <DragOutlined />
                        </ListItemIcon>
                        <ListItemText primary={column.headerName} />
                        <Checkbox
                          checked={column.visible}
                          onChange={() => handleToggleColumn(column.field)}
                          icon={<EyeInvisibleOutlined />}
                          checkedIcon={<EyeOutlined />}
                        />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>

        {/* Hidden Columns */}
        {hiddenColumns.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Hidden Columns ({hiddenColumns.length})
            </Typography>
            <List
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              {hiddenColumns.map((column) => (
                <ListItem
                  key={column.field}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EyeInvisibleOutlined />
                  </ListItemIcon>
                  <ListItemText primary={column.headerName} />
                  <Checkbox
                    checked={column.visible}
                    onChange={() => handleToggleColumn(column.field)}
                    icon={<EyeInvisibleOutlined />}
                    checkedIcon={<EyeOutlined />}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
} 